/**
 * Batch Details Component
 * Detailed view of a specific batch with file-by-file progress
 */
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  Download,
  CheckCircle,
  Error,
  HourglassEmpty,
  CloudUpload,
  PlayArrow,
  Replay,
} from '@mui/icons-material';
import { useUpload } from '@/contexts/UploadContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatDateFull } from '@/utils/dateUtils';
import uploadApiService from '@/services/uploadApi';
import { getApiBaseUrl } from '@/utils/apiUrl';

const BatchDetails: React.FC = () => {
  console.log('🔍 BatchDetails component MOUNTED');
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentBatch, loading, error, loadBatchStatus, clearError } = useUpload();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [reuploading, setReuploading] = React.useState(false);
  const [forceFinalizing, setForceFinalizing] = React.useState(false);
  const [reprocessing, setReprocessing] = React.useState(false);
  const [lastRefreshAt, setLastRefreshAt] = React.useState<number>(0);
  const [now, setNow] = React.useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  
  console.log('🔍 BatchDetails - State:', {
    batchId,
    loading,
    error,
    currentBatch: currentBatch ? {
      batch_id: currentBatch.batch_id,
      status: currentBatch.status
    } : null
  });
  
  // Load batch on mount and poll for updates
  const handleLoadBatch = React.useCallback(async () => {
    if (batchId) {
      await loadBatchStatus(batchId);
      setLastRefreshAt(Date.now());
    }
  }, [batchId, loadBatchStatus]);

  useEffect(() => {
    if (!batchId) return;
    handleLoadBatch();
    const interval = setInterval(handleLoadBatch, 1000);
    return () => clearInterval(interval);
  }, [batchId, handleLoadBatch]);
  
  if (loading && !currentBatch) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }
  
  if (error && !currentBatch) {
    return (
      <Alert severity="error" onClose={clearError}>
        {error}
      </Alert>
    );
  }
  
  if (!currentBatch) {
    return (
      <Alert severity="warning">
        {t('upload.details.not_found', 'Batch not found')}
      </Alert>
    );
  }
  
  // Debug: log batch status for troubleshooting download button (immediate log)
  console.log('🔍 BatchDetails RENDER - currentBatch:', {
    batch_id: currentBatch.batch_id,
    status: currentBatch.status,
    statusType: typeof currentBatch.status,
    statusValue: JSON.stringify(currentBatch.status),
    articles_extracted: currentBatch.articles_extracted,
    combined_csv_path: currentBatch.combined_csv_path,
    openai_file_id: currentBatch.openai_file_id,
    shouldShowDownload: currentBatch.status === 'completed',
    statusComparison: currentBatch.status === 'completed' ? '✅ MATCH' : '❌ NO MATCH',
    conditionResult: currentBatch.status === 'completed'
  });
  
  // Removed debug useEffect to fix React error #310 - logs in render are sufficient
  
  // Detect stuck files (same status_details for more than 2 minutes)
  const isFileStuck = (file: any): boolean => {
    if (file.status === 'completed' || file.status === 'failed') {
      return false; // Completed/failed files are not stuck
    }
    
    if (!file.processing_started_at && !file.uploaded_at) {
      return false; // Can't determine if stuck without start time
    }
    
    const startTimeStr = file.processing_started_at || file.uploaded_at;
    if (!startTimeStr) {
      return false;
    }
    
    const startTime = new Date(startTimeStr).getTime();
    const now = Date.now();
    const minutesElapsed = (now - startTime) / (1000 * 60);
    
    // Consider stuck if processing for more than 2 minutes without status change
    return minutesElapsed > 2;
  };
  
  // Translate status details
  const translateStatusDetails = (statusDetails: string, _status: string): string => {
    // Handle "Extracting text from PDF file" (English)
    if (statusDetails.toLowerCase().includes('extracting text from pdf')) {
      return t('upload.details.status.extracting_text', 'Extraction du texte du PDF');
    }
    
    // Handle "Starting enrichment process" or similar (English)
    if (statusDetails.toLowerCase().includes('starting enrichment')) {
      return t('upload.details.status.starting_enrichment', 'Démarrage de l\'enrichissement...');
    }
    
    // Handle "Démarrage de l'enrichissement..." (already in French)
    if (statusDetails.includes('Démarrage de l\'enrichissement')) {
      return statusDetails; // Already translated
    }
    
    // Handle "Processing page X/Y (Z%) - N articles" (English format)
    const pageMatch = statusDetails.match(/Processing page (\d+)\/(\d+) \(([\d.]+)%\) - (\d+) (article|media coverage item)/i);
    if (pageMatch) {
      const [, current, total, percent, articles] = pageMatch;
      return t('upload.details.status.processing_page', 'Traitement de la page {{current}}/{{total}} ({{percent}}%) - {{articles}} retombée(s)', {
        current,
        total,
        percent,
        articles
      });
    }
    
    // Handle "Traitement de la page X/Y" (already in French format)
    const pageMatchFr = statusDetails.match(/Traitement de la page (\d+)\/(\d+)/);
    if (pageMatchFr) {
      return statusDetails; // Already translated
    }
    
    // Handle "Processing: enriching" or similar
    if (statusDetails.toLowerCase().includes('processing:')) {
      const step = statusDetails.split(':')[1]?.trim();
      if (step) {
        return t(`upload.details.status.${step}`, step);
      }
    }
    
    // Default: return as-is (might already be translated)
    return statusDetails;
  };
  
  // Translate status label
  const translateStatus = (status: string): string => {
    return t(`upload.details.status.${status}`, status);
  };
  
  // Get status color
  const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
      case 'enriching':
      case 'extracting':
        return 'primary';
      case 'uploading':
      case 'uploaded':
      case 'queued':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Format date
  // Using Montreal timezone from utils
  const formatDate = formatDateFull;
  
  // Calculate processing time
  const getProcessingTime = (startDate: string, endDate?: string) => {
    try {
      const start = new Date(startDate);
      const end = endDate ? new Date(endDate) : new Date();
      const seconds = Math.floor((end.getTime() - start.getTime()) / 1000);
      
      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
      return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
    } catch {
      return 'N/A';
    }
  };
  
  // Calculate time elapsed since start
  const getTimeElapsed = (startDate: string) => {
    try {
      const start = new Date(startDate);
      const now = new Date();
      const seconds = Math.floor((now.getTime() - start.getTime()) / 1000);
      
      if (seconds < 60) return `${seconds} ${t('upload.details.seconds', 'secondes')}`;
      if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        return `${minutes} ${minutes === 1 ? t('upload.details.minute', 'minute') : t('upload.details.minutes', 'minutes')}`;
      }
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (minutes === 0) {
        return `${hours} ${hours === 1 ? t('upload.details.hour', 'heure') : t('upload.details.hours', 'heures')}`;
      }
      return `${hours}h ${minutes}min`;
    } catch {
      return '';
    }
  };
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/upload/batches')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">{t('upload.details.title', 'Batch Details')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {currentBatch.batch_id}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {lastRefreshAt > 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('upload.details.last_update', 'MAJ il y a')} {Math.floor((now - lastRefreshAt) / 1000)}s
            </Typography>
          )}
          <Tooltip title={t('upload.details.refresh', 'Actualiser')}>
            <IconButton onClick={handleLoadBatch}>
              <Refresh />
            </IconButton>
          </Tooltip>
          {currentBatch.status === 'processing' && (() => {
            const batchAgeMin = currentBatch.created_at
              ? (Date.now() - new Date(currentBatch.created_at).getTime()) / (1000 * 60)
              : 0;
            const hasStuckFiles = (currentBatch.files || []).filter(isFileStuck).length > 0;
            const canForceFinalize = batchAgeMin > 2 || currentBatch.files_processed > 0;
            return (
              <>
                {canForceFinalize && (
                  <Tooltip title={t('upload.details.force_finalize_tooltip', 'Forcer l\'agrégation avec les fichiers disponibles')}>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={<PlayArrow />}
                      disabled={forceFinalizing}
                      onClick={async () => {
                        if (!confirm(t('upload.details.force_finalize_confirm', 'Forcer la finalisation avec les fichiers actuellement traités? Les fichiers encore en cours seront exclus.'))) return;
                        setForceFinalizing(true);
                        try {
                          await uploadApiService.forceFinalizeBatch(currentBatch.batch_id);
                          if (batchId) loadBatchStatus(batchId);
                        } catch (e: any) {
                          alert(e?.response?.data?.detail || e?.message || t('upload.details.force_finalize_error', 'Erreur'));
                        } finally { setForceFinalizing(false); }
                      }}
                    >
                      {forceFinalizing ? '...' : t('upload.details.force_finalize', 'Forcer la finalisation')}
                    </Button>
                  </Tooltip>
                )}
                {hasStuckFiles && (
                  <Tooltip title={t('upload.details.reprocess_tooltip', 'Relancer le traitement des fichiers bloqués')}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      startIcon={<Replay />}
                      disabled={reprocessing}
                      onClick={async () => {
                        if (!confirm(t('upload.details.reprocess_confirm', 'Relancer le traitement des fichiers bloqués?'))) return;
                        setReprocessing(true);
                        try {
                          const res = await uploadApiService.reprocessStuckFiles(currentBatch.batch_id);
                          alert(t('upload.details.reprocess_success', '{{count}} fichier(s) relancé(s)', { count: res.republished.extract + res.republished.enrich }));
                          if (batchId) loadBatchStatus(batchId);
                        } catch (e: any) {
                          alert(e?.response?.data?.detail || e?.message || t('upload.details.reprocess_error', 'Erreur'));
                        } finally { setReprocessing(false); }
                        }}
                    >
                      {reprocessing ? '...' : t('upload.details.reprocess', 'Reprocesser les fichiers bloqués')}
                    </Button>
                  </Tooltip>
                )}
              </>
            );
          })()}
          {(() => {
            const shouldShow = currentBatch.status === 'completed';
            console.log('🔍 Download button condition check:', {
              status: currentBatch.status,
              shouldShow,
              combined_csv_path: currentBatch.combined_csv_path,
              openai_file_id: currentBatch.openai_file_id
            });
            return shouldShow ? (
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={async () => {
                try {
                  // Generate filename
                  const filename = `${currentBatch.client_name}_${new Date(currentBatch.created_at).toISOString().split('T')[0].replace(/-/g, '')}_${currentBatch.batch_id.substring(0, 8)}.csv`;
                  
                  // Get API base URL using shared utility
                  const apiBaseUrl = getApiBaseUrl();
                  const token = localStorage.getItem('access_token');
                  
                  if (!token) {
                    alert(t('upload.details.download_error', 'Authentication required. Please log in again.'));
                    return;
                  }
                  
                  // Fetch file with authentication
                  const downloadEndpoint = apiBaseUrl + '/api/batches/' + currentBatch.batch_id + '/download';
                  const response = await fetch(downloadEndpoint, {
                    method: 'GET',
                    headers: {
                      'Authorization': 'Bearer ' + token
                    },
                    credentials: 'include' // Include credentials for CORS
                  });
                  
                  if (!response.ok) {
                    // Try to get error message from JSON response if available
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                      const errorData = await response.json();
                      const errorMsg = errorData.detail || errorData.message || `Download failed: ${response.status} ${response.statusText}`;
                      throw new globalThis.Error(errorMsg);
                    }
                    const errorMsg = 'Download failed: ' + response.status + ' ' + response.statusText;
                    throw new globalThis.Error(errorMsg);
                  }
                  
                  // Verify Content-Type is CSV before reading blob
                  const contentType = response.headers.get('content-type') || '';
                  console.log('📥 Download response:', {
                    status: response.status,
                    contentType: contentType,
                    ok: response.ok
                  });
                  
                  if (contentType.includes('application/json')) {
                    // If we get JSON instead of CSV, it's an error
                    const errorData = await response.json();
                    const errorMsg = errorData.detail || errorData.message || 'Server returned JSON instead of CSV';
                    console.error('❌ Server returned JSON instead of CSV:', errorData);
                    throw new globalThis.Error(errorMsg);
                  }
                  
                  if (!contentType.includes('text/csv') && !contentType.includes('application/octet-stream')) {
                    console.warn('⚠️ Unexpected Content-Type:', contentType, '- proceeding anyway');
                  }
                  
                  // Clone response before reading blob to avoid consuming it
                  const responseClone = response.clone();
                  
                  // Get blob from response
                  const blob = await responseClone.blob();
                  
                  // Verify blob is not empty
                  if (blob.size === 0) {
                    console.error('❌ Downloaded blob is empty');
                    throw new globalThis.Error('Downloaded file is empty');
                  }
                  
                  console.log('✅ Blob downloaded:', {
                    size: blob.size,
                    type: blob.type,
                    contentType: contentType
                  });
                  
                  // Create blob URL and trigger download
                  const blobUrl = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = blobUrl;
                  link.download = filename;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  
                  // Clean up blob URL
                  window.URL.revokeObjectURL(blobUrl);
                  
                  console.log('✅ Download completed:', filename);
                } catch (error) {
                  console.error('❌ Download failed:', error);
                  alert(t('upload.details.download_error', 'Failed to download file. Please try again.'));
                }
              }}
            >
              {t('upload.details.download', 'Download')}
            </Button>
            ) : null;
          })()}
          {isAdmin && currentBatch.status === 'completed' && currentBatch.combined_csv_path && (
            <Tooltip title={t('upload.details.reupload_tooltip', 'Ré-uploader cette batch vers l\'assistant OpenAI')}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CloudUpload />}
                disabled={reuploading}
                onClick={async () => {
                  if (!confirm(t('upload.details.reupload_confirm', 'Voulez-vous ré-uploader cette batch vers l\'assistant OpenAI? Cette action remplacera le fichier actuel dans l\'assistant.'))) {
                    return;
                  }
                  
                  setReuploading(true);
                  try {
                    const result = await uploadApiService.reuploadBatchToAssistant(currentBatch.batch_id);
                    alert(t('upload.details.reupload_success', 'Batch ré-uploadée avec succès! Nouveau fichier ID: {fileId}', { fileId: result.openai_file_id }));
                    // Refresh batch status to get updated openai_file_id
                    if (batchId) {
                      loadBatchStatus(batchId);
                    }
                  } catch (error: any) {
                    console.error('❌ Re-upload failed:', error);
                    alert(t('upload.details.reupload_error', 'Échec du ré-upload: {error}', { error: error.message || error.detail || 'Erreur inconnue' }));
                  } finally {
                    setReuploading(false);
                  }
                }}
              >
                {reuploading 
                  ? t('upload.details.reuploading', 'Ré-upload en cours...')
                  : t('upload.details.reupload', 'Ré-upload vers l\'assistant')
                }
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('upload.details.client', 'Client')}
              </Typography>
              <Typography variant="h6">{currentBatch.client_name}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('upload.details.uploaded_by', 'Uploaded By')}
              </Typography>
              <Typography variant="h6">{currentBatch.uploaded_by_name}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('upload.details.table.status', 'Status')}
              </Typography>
              <Chip label={translateStatus(currentBatch.status)} color={getStatusColor(currentBatch.status)} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('upload.details.progress', 'Progress')}
              </Typography>
              <Typography variant="h6">{currentBatch.progress_percentage}%</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Progress Bar */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('upload.details.overall_progress', 'Overall Progress')}
        </Typography>
        {currentBatch.status === 'processing' && currentBatch.files_processed < currentBatch.total_files && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('upload.details.processing_note', 'Le traitement est en cours. Les fichiers sont regroupés pour un traitement optimisé et la progression sera mise à jour automatiquement.')}
          </Alert>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={currentBatch.progress_percentage}
            sx={{ flexGrow: 1, height: 12, borderRadius: 6 }}
          />
          <Typography variant="body2" color="text.secondary">
            {currentBatch.files_processed} / {currentBatch.total_files}
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">
              {t('upload.details.files_uploaded', 'Files Uploaded')}: {currentBatch.files_uploaded}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">
              {t('upload.details.files_processed', 'Files Processed')}: {currentBatch.files_processed}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" color="text.secondary">
              {t('upload.details.articles', 'Retombées Extraites')}: {currentBatch.articles_extracted ?? 0}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Files Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">
            {t('upload.details.files', { count: currentBatch.files.length })}
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('upload.details.table.filename', 'Filename')}</TableCell>
                <TableCell>{t('upload.details.table.status', 'Status')}</TableCell>
                <TableCell align="right">{t('upload.details.table.size', 'Size')}</TableCell>
                <TableCell align="right">{t('upload.details.table.pages', 'Pages')}</TableCell>
                <TableCell align="right">{t('upload.details.table.articles', 'Retombées')}</TableCell>
                <TableCell align="right">{t('upload.details.table.time', 'Processing Time')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentBatch.files.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      {t('upload.details.no_files', 'No files in this batch')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                // Sort files by ID (stable order) and then by status (completed first, then processing, then others)
                [...currentBatch.files]
                  .sort((a, b) => {
                    // First, sort by status priority: completed > processing > others
                    const statusPriority: Record<string, number> = {
                      completed: 1,
                      processing: 2,
                      enriching: 3,
                      extracting: 4,
                      queued: 5,
                      uploaded: 6,
                      uploading: 7,
                      failed: 8,
                    };
                    const aPriority = statusPriority[a.status] || 99;
                    const bPriority = statusPriority[b.status] || 99;
                    if (aPriority !== bPriority) {
                      return aPriority - bPriority;
                    }
                    // Then sort by ID for stable ordering
                    const aId = typeof a.id === 'string' ? parseInt(a.id, 10) : (a.id || 0);
                    const bId = typeof b.id === 'string' ? parseInt(b.id, 10) : (b.id || 0);
                    return aId - bId;
                  })
                  .map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {file.status === 'completed' ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : file.status === 'failed' ? (
                          <Error color="error" fontSize="small" />
                        ) : (
                          <HourglassEmpty color="action" fontSize="small" />
                        )}
                        <Box>
                          <Typography variant="body2">{file.filename}</Typography>
                          {file.status_details && (
                            <Typography variant="caption" color="text.secondary">
                              {translateStatusDetails(file.status_details, file.status)}
                            </Typography>
                          )}
                          {isFileStuck(file) && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                              ⚠️ {t('upload.details.status.stuck', 'Traitement en cours depuis plus de 2 minutes...')}
                            </Typography>
                          )}
                          {file.status === 'enriching' && (!file.status_details || file.status_details.toLowerCase().includes('starting')) && (
                            <Box>
                              <Typography variant="caption" color="info.main" sx={{ fontStyle: 'italic', display: 'block' }}>
                                {t('upload.details.processing_in_progress', 'Traitement en cours...')}
                              </Typography>
                              {(file.processing_started_at || file.uploaded_at) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {t('upload.details.started_at', 'Démarré il y a')} {getTimeElapsed(file.processing_started_at || file.uploaded_at)}
                                </Typography>
                              )}
                            </Box>
                          )}
                          {file.error_message && (
                            <Typography variant="caption" color="error">
                              {file.error_message}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={translateStatus(file.status)} size="small" color={getStatusColor(file.status)} />
                    </TableCell>
                    <TableCell align="right">
                      {(file.file_size_bytes / 1024 / 1024).toFixed(2)} MB
                    </TableCell>
                    <TableCell align="right">
                      {file.pages_extracted !== null && file.pages_extracted !== undefined 
                        ? file.pages_extracted 
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {file.articles_extracted !== null && file.articles_extracted !== undefined 
                        ? file.articles_extracted 
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {file.processing_time_seconds
                        ? `${file.processing_time_seconds}s`
                        : file.uploaded_at && file.completed_at
                        ? getProcessingTime(file.uploaded_at, file.completed_at)
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Metadata */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('upload.details.metadata', 'Metadata')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              {t('upload.details.created_at', 'Created At')}
            </Typography>
            <Typography variant="body1">{formatDate(currentBatch.created_at)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              {t('upload.details.updated_at', 'Updated At')}
            </Typography>
            <Typography variant="body1">{formatDate(currentBatch.updated_at)}</Typography>
          </Grid>
          {currentBatch.completed_at && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t('upload.details.completed_at', 'Completed At')}
              </Typography>
              <Typography variant="body1">{formatDate(currentBatch.completed_at)}</Typography>
            </Grid>
          )}
          {currentBatch.openai_file_id && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {t('upload.details.openai_file', 'OpenAI File ID')}
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                {currentBatch.openai_file_id}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

// Log after all imports and before export
// Module loaded - logging removed to reduce console noise

export default BatchDetails;

